import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Search } from "lucide-react";
import { findGameLevel, formatNumber } from "@/lib/api";

export const LevelJumpBox = ({ maxLevel, game = "pet-rescue", gameName = "Pet Rescue Saga" }) => {
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const n = parseInt(value, 10);
    if (!n || n < 1) return;
    setLoading(true);
    setResult(null);
    try {
      const data = await findGameLevel(game, n);
      if (data.items.length === 1) navigate(`/video/${data.items[0].id}`);
      else setResult(data);
    } catch {
      setResult({ level: n, items: [], error: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-8 max-w-xl" data-testid="level-jump-box">
      <div className="mono-label text-white/50">Jump to a level</div>
      <form onSubmit={submit} className="mt-3 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input
            type="number"
            inputMode="numeric"
            min="1"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={maxLevel ? `Level number, e.g. 206 (up to ${maxLevel.toLocaleString()})` : "Level number"}
            aria-label={`${gameName} level number`}
            data-testid="level-jump-input"
            className="w-full h-12 rounded-full bg-[#12141d] border border-white/10 pl-11 pr-4 text-[#f4f1ea] placeholder:text-white/30 focus:outline-none focus:border-[#d95d39]/60 transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !value}
          data-testid="level-jump-submit"
          className="h-12 px-6 rounded-full bg-[#d95d39] text-[#090a0f] font-mono-tech text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-[#e8714e] disabled:opacity-50 transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          Go
        </button>
      </form>
      {result && <JumpResult result={result} />}
    </div>
  );
};

function JumpResult({ result }) {
  if (!result.items.length) {
    return (
      <p className="mt-3 text-sm text-white/50" data-testid="level-jump-empty">
        {result.error ? "Something went wrong — try again." : `No video found for level ${result.level} yet.`}
      </p>
    );
  }
  return (
    <div className="mt-4 space-y-2" data-testid="level-jump-results">
      <div className="text-sm text-white/50">
        {result.items.length} videos for level {result.level} — pick one:
      </div>
      {result.items.map((v) => (
        <Link
          key={v.id}
          to={`/video/${v.id}`}
          data-testid={`level-jump-result-${v.id}`}
          className="flex items-center gap-3 rounded-lg border border-white/10 bg-[#12141d] p-2 hover:border-[#d95d39]/50 transition-colors"
        >
          <img src={v.thumbnail} alt="" loading="lazy" className="w-24 aspect-video object-cover rounded" />
          <div className="min-w-0">
            <div className="text-sm text-[#f4f1ea] line-clamp-2">{v.title}</div>
            <div className="mono-label text-white/40 mt-1">{formatNumber(v.view_count)} views</div>
          </div>
        </Link>
      ))}
    </div>
  );
}
